import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../app/hooks";
import { isMobileSelector } from "../app/core-slice";
import { useMemo } from "react";
import { MAIN_NAV, NavElement, NavModule } from "./nav";
import { selectSessionUser } from "../features/auth/auth.slice";

interface NavSectionProps {
    module: NavModule;
}

function NavSection({ module }: NavSectionProps) {
    return (
        <>
            <div className="sb-sidenav-menu-heading">{module.name}</div>
            {module.elements.map((sn) => (
                <NavLinkGroup element={sn} key={sn.id} />
            ))}
        </>
    );
}

interface NavGroupProps {
    element: NavElement;
}

function NavLinkGroup({ element }: NavGroupProps) {
    if (element.subElements && element.subElements.length) {
        return (
            <>
                <a
                    className="nav-link collapsed"
                    href="#"
                    data-bs-toggle="collapse"
                    data-bs-target={`#${element.id}`}
                    aria-expanded="false"
                    aria-controls={element.id}
                >
                    <div className="sb-nav-link-icon">
                        <i className={`fas ${element.icon}`} />
                    </div>
                    {element.name}
                    <div className="sb-sidenav-collapse-arrow">
                        <i className="fas fa-angle-down" />
                    </div>
                </a>
                <div
                    className="collapse"
                    id={element.id}
                    aria-labelledby="headingOne"
                    data-bs-parent="#sidenavAccordion"
                >
                    <nav className="sb-sidenav-menu-nested nav">
                        {element.subElements.map((subElement) => (
                            <NavLinkGroup element={subElement} key={subElement.id} />
                        ))}
                    </nav>
                </div>
            </>
        );
    }

    return <NavLinks item={element} />;
}

interface NavLinkProps {
    item: NavElement;
}

function NavLinks({ item }: NavLinkProps) {
    const isMobile = useAppSelector(isMobileSelector);
    const location = useLocation();

    const isActivePath = (path: string) => {
        return location.pathname.toLowerCase() == path.toLowerCase();
    };

    const toggleSideNav = () => {
        isMobile && document.body.classList.toggle("sb-sidenav-toggled");
    };

    return (
        <Link
            className={`nav-link ${isActivePath(item.absolutePath!) ? "active" : ""}`}
            to={item.absolutePath!}
            onClick={toggleSideNav}
        >
            <div className="sb-nav-link-icon">
                <i className={`fas ${item.icon}`} />
            </div>
            {item.name}
        </Link>
    );
}

export default function SideNav() {
    const isMobile = useAppSelector(isMobileSelector);
    const sessionUser = useAppSelector(selectSessionUser);

    const toggleSideNav = () => {
        isMobile && document.body.classList.toggle("sb-sidenav-toggled");
    };

    function getEligibleNavElements(nav: NavElement[], parentName: string, parentPath: string): NavElement[] {
        return nav
            .filter(
                (n) =>
                    !n.hideInMenu &&
                    (!n.feature || sessionUser?.hasFeature(n.feature)) &&
                    (n.allowAnonymous ||
                        (!n.permission && !!sessionUser) ||
                        (sessionUser && sessionUser.hasPermission(n.permission || ""))),
            )
            .map((n): NavElement => {
                const id = `${parentName}_${n.name}`.replaceAll(" ", "_");
                const absolutePath = `${parentPath}/${n.path}`;
                const subElements: NavElement[] | undefined = n.subElements
                    ? getEligibleNavElements(n.subElements, id, absolutePath)
                    : undefined;

                return {
                    ...n,
                    id,
                    absolutePath,
                    subElements,
                };
            })
            .filter((n) => !n.subElements || n.subElements.length > 0);
    }

    const nav = useMemo(() => {
        const eligibleFeatures = MAIN_NAV.filter(
            (n) => !n.hideInMenu && (!n.feature || sessionUser?.hasFeature(n.feature)),
        );

        const menu = eligibleFeatures.map((module) => ({
            ...module,
            elements: getEligibleNavElements(module.elements, module.name, `/${module.path}`),
        }));

        return menu.filter((n) => n.elements.length > 0);
    }, [sessionUser]);

    return (
        <div id="layoutSidenav_nav">
            <nav className="sb-sidenav accordion sb-sidenav-dark" id="sidenavAccordion">
                <div className="sb-sidenav-menu">
                    <div className="nav">
                        <NavLinks
                            item={{
                                name: "Dashboard",
                                icon: "fa-tachometer-alt",
                                path: "dashboard",
                                absolutePath: "/home/dashboard",
                            }}
                        />
                        {nav.map((n, i) => (
                            <NavSection module={n} key={i} />
                        ))}
                    </div>
                </div>
                {sessionUser && (
                    <div className="sb-sidenav-footer">
                        <div className="small">Logged in as:</div>
                        {`${sessionUser.givenName} | ${sessionUser.role}`}
                    </div>
                )}
            </nav>
        </div>
    );
}
